/**
 * Deliverable Sets API Module
 *
 * This module provides API functions for fetching deliverable sets data.
 * Uses the customInstance function from auth feature's axios-config for
 * enhanced error handling, retry logic, and cancellation support.
 *
 * All requests automatically include:
 * - JWT Bearer token from localStorage (if available)
 * - Global 401/403 error handling with redirect to login
 * - Automatic retry logic for network/5xx errors
 * - Request cancellation support via promise.cancel()
 */

import { BodyType, customInstance } from '@/features/auth/axios-config'
import {
  RequestApprovalDeliverableSet,
  RequestCancelApprovalRequest,
  RequestCreateDeliverableSet,
  RequestDelegateReview,
  RequestDraftApprovalDeliverableSet,
  RequestFilters,
  RequestUpdateDeliverableSet,
  SuccessResponseListBool,
  SuccessResponseListResponseDeliverableSet,
} from '@/schema'
import { mergeTablePaginationParams } from '@/utils'

const API_ENDPOINT_DELIVERABLE_SETS = '/admin/v1/deliverable-sets'
const buildApiById = (id: string) => `${API_ENDPOINT_DELIVERABLE_SETS}/${id}`

export type SecondParameter<T extends (...args: never) => unknown> =
  Parameters<T>[1]

export const deliverableSetListApi = {
  /**
   * Fetches all deliverable sets
   * @param search - Optional search query (minimum 3 characters)
   * @returns Promise with deliverable sets data and total count
   */
  getDeliverableSetList: async (
    data: Partial<RequestFilters> = {}
  ): Promise<SuccessResponseListResponseDeliverableSet> => {
    const requestBody = mergeTablePaginationParams(data)

    return customInstance<SuccessResponseListResponseDeliverableSet>({
      method: 'post',
      url: `${API_ENDPOINT_DELIVERABLE_SETS}/list`,
      data: requestBody,
    })
  },

  /**
   * Fetches a single deliverable set by ID
   * @param id - Deliverable set ID
   * @returns Promise with deliverable set data
   */
  getDeliverableSetById: async (
    id: string
  ): Promise<SuccessResponseListResponseDeliverableSet> => {
    return customInstance<SuccessResponseListResponseDeliverableSet>({
      method: 'get',
      url: buildApiById(id),
    })
  },

  /**
   * Creates a new deliverable set
   * @param data - Deliverable set data
   * @returns Promise with created deliverable set
   */
  createDeliverableSet: async (
    data: RequestCreateDeliverableSet
  ): Promise<SuccessResponseListResponseDeliverableSet> => {
    return customInstance<SuccessResponseListResponseDeliverableSet>({
      method: 'post',
      url: API_ENDPOINT_DELIVERABLE_SETS,
      data,
    })
  },

  /**
   * Updates an existing deliverable set
   * @param id - Deliverable set ID
   * @param data - Updated deliverable set data
   * @returns Promise with updated deliverable set
   */
  updateDeliverableSet: async (
    id: string,
    data: RequestUpdateDeliverableSet
  ): Promise<SuccessResponseListResponseDeliverableSet> => {
    return customInstance<SuccessResponseListResponseDeliverableSet>({
      method: 'put',
      url: buildApiById(id),
      data,
    })
  },

  /**
   * Deletes a deliverable set
   * @param id - Deliverable set ID
   * @returns Promise with deletion confirmation
   */
  deleteDeliverableSet: async (
    id: string
  ): Promise<SuccessResponseListBool> => {
    return customInstance<SuccessResponseListBool>({
      method: 'delete',
      url: buildApiById(id),
    })
  },

  sendToReview: async (
    id: string,
    data: RequestApprovalDeliverableSet
  ): Promise<SuccessResponseListResponseDeliverableSet> => {
    return customInstance<SuccessResponseListResponseDeliverableSet>({
      method: 'post',
      url: `${buildApiById(id)}/send-approval-request`,
      data,
    })
  },

  cancelApprovalRequest: async (
    id: string,
    data: RequestCancelApprovalRequest
  ): Promise<SuccessResponseListResponseDeliverableSet> => {
    return customInstance<SuccessResponseListResponseDeliverableSet>({
      method: 'post',
      url: `${buildApiById(id)}/cancel-approval-request`,
      data,
    })
  },

  delegateReview: async (
    data: RequestDelegateReview
  ): Promise<SuccessResponseListResponseDeliverableSet> => {
    return customInstance<SuccessResponseListResponseDeliverableSet>({
      method: 'post',
      url: `${API_ENDPOINT_DELIVERABLE_SETS}/delegate-reviewer`,
      data,
    })
  },

  /**
   * @summary Move deliverable set back to draft from in_review
   */
  draftApprovalRequest: async (
    deliverableSetId: string,
    requestDraftApprovalDeliverableSet: BodyType<RequestDraftApprovalDeliverableSet>,
    options?: SecondParameter<typeof customInstance>,
    signal?: AbortSignal
  ) => {
    return customInstance<SuccessResponseListResponseDeliverableSet>(
      {
        url: `${buildApiById(deliverableSetId)}/draft-approval-request`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: requestDraftApprovalDeliverableSet,
        signal,
      },
      options
    )
  },
}
